import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { KeyValuePairList } from './key-value-pair-list';
import type {
    AuthFormData,
    WizardAuthType,
    ApiKeyDelivery,
    OAuth2GrantType,
    OAuth2AuthMethod,
} from '@/types/integration-form-types';
import { Shield, Key, Lock, Globe } from 'lucide-react';

interface AuthConfigPanelProps {
    value: AuthFormData;
    onChange: (auth: AuthFormData) => void;
    disabled?: boolean;
}

const AUTH_TYPE_OPTIONS: { value: WizardAuthType; label: string; icon: typeof Shield; desc: string }[] = [
    { value: 'api_key', label: 'API Key', icon: Key, desc: 'Authenticate with an API key' },
    { value: 'basic_auth', label: 'Basic Auth', icon: Lock, desc: 'HTTP Basic username/password' },
    { value: 'bearer_token', label: 'Bearer Token', icon: Key, desc: 'Authenticate with a bearer token' },
    { value: 'oauth2', label: 'OAuth 2.0', icon: Globe, desc: 'OAuth2 authorization flow' },
];

function FieldGroup({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-sm font-medium">
                {label}
                {required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            {children}
        </div>
    );
}

export function AuthConfigPanel({ value, onChange, disabled = false }: AuthConfigPanelProps) {
    const update = (patch: Partial<AuthFormData>) => onChange({ ...value, ...patch });

    const enabled = value.enabledAuthTypes ?? [];
    const isEnabled = (type: WizardAuthType) => enabled.includes(type);
    const activeView = value.authType;
    const hasAnyEnabled = enabled.length > 0;
    const hasConfigPanel = hasAnyEnabled && activeView !== 'none';

    const toggleAuthType = (type: WizardAuthType, on: boolean) => {
        let next: WizardAuthType[];
        if (on) {
            next = [...enabled, type];
        } else {
            next = enabled.filter((t) => t !== type);
        }
        let nextActive = activeView;
        if (on) {
            nextActive = type;
        } else if (activeView === type) {
            nextActive = next.length > 0 ? next[0] : 'none';
        }
        update({ enabledAuthTypes: next, authType: nextActive });
    };

    return (
        <div className="grid grid-cols-[260px_1fr] gap-6">
            {/* Left: Auth Type Selector with toggles */}
            <div className="space-y-2">
                <Label className="text-sm font-semibold">Auth Types</Label>
                <p className="text-[11px] text-muted-foreground leading-tight pb-1">
                    Enable auth types this integration supports. Click a row to configure.
                </p>
                <div className="space-y-1.5">
                    {AUTH_TYPE_OPTIONS.map((opt) => {
                        const Icon = opt.icon;
                        const on = isEnabled(opt.value);
                        const isActive = activeView === opt.value && on;
                        return (
                            <div
                                key={opt.value}
                                className={`flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 transition-colors ${
                                    isActive
                                        ? 'border-blue-400 bg-blue-50 ring-1 ring-blue-400'
                                        : on
                                            ? 'border-emerald-400 bg-emerald-100'
                                            : 'border-slate-200'
                                } ${disabled ? 'opacity-50' : ''}`}
                            >
                                <Switch
                                    checked={on}
                                    onCheckedChange={(checked) => {
                                        if (disabled) return;
                                        toggleAuthType(opt.value, checked);
                                    }}
                                    disabled={disabled}
                                />
                                <button
                                    type="button"
                                    disabled={disabled || !on}
                                    onClick={() => {
                                        if (on) update({ authType: opt.value });
                                    }}
                                    className={`flex flex-1 items-center gap-2.5 text-left ${
                                        disabled || !on ? 'cursor-default' : 'cursor-pointer'
                                    } ${!on ? 'opacity-50' : ''}`}
                                >
                                    <div className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0 ${
                                        isActive ? 'bg-blue-100 text-blue-600' : on ? 'bg-emerald-200 text-emerald-700' : 'bg-slate-100 text-slate-500'
                                    }`}>
                                        <Icon size={14} />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium leading-tight">{opt.label}</p>
                                        <p className="text-[11px] text-muted-foreground leading-tight">{opt.desc}</p>
                                    </div>
                                </button>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Right: Configuration Details */}
            <div className="space-y-5">
                {!hasAnyEnabled && (
                    <div className="flex h-full items-center justify-center rounded-lg border-2 border-dashed border-slate-200 p-8">
                        <p className="text-sm text-slate-400 text-center">
                            No authentication enabled. Toggle on one or more auth types to configure.
                        </p>
                    </div>
                )}

                {hasAnyEnabled && activeView === 'none' && (
                    <div className="flex h-full items-center justify-center rounded-lg border-2 border-dashed border-slate-200 p-8">
                        <p className="text-sm text-slate-400 text-center">
                            Click an enabled auth type on the left to configure it.
                        </p>
                    </div>
                )}

                {/* API Key Config */}
                {activeView === 'api_key' && isEnabled('api_key') && (
                    <div className="space-y-4 rounded-lg border border-slate-200 p-4">
                        <h4 className="text-sm font-semibold text-slate-700">API Key Delivery</h4>
                        <RadioGroup
                            value={value.apiKeyDelivery}
                            onValueChange={(v) => update({ apiKeyDelivery: v as ApiKeyDelivery })}
                            disabled={disabled}
                            className="grid grid-cols-2 gap-2"
                        >
                            {[
                                { value: 'default', label: 'Default (Authorization header)' },
                                { value: 'bearer_token', label: 'Bearer Token' },
                                { value: 'query', label: 'Query Parameter' },
                                { value: 'header', label: 'Custom Header' },
                            ].map((opt) => (
                                <div key={opt.value} className="flex items-center gap-2">
                                    <RadioGroupItem value={opt.value} id={`apikey-${opt.value}`} />
                                    <Label htmlFor={`apikey-${opt.value}`} className="text-sm font-normal cursor-pointer">
                                        {opt.label}
                                    </Label>
                                </div>
                            ))}
                        </RadioGroup>
                        {(value.apiKeyDelivery === 'query' || value.apiKeyDelivery === 'header') && (
                            <FieldGroup
                                label={value.apiKeyDelivery === 'query' ? 'Parameter Name' : 'Header Name'}
                                required
                            >
                                <Input
                                    placeholder={value.apiKeyDelivery === 'query' ? 'e.g. api_key' : 'e.g. X-API-Key'}
                                    value={value.apiKeyParamName}
                                    onChange={(e) => update({ apiKeyParamName: e.target.value })}
                                    disabled={disabled}
                                    className="font-mono text-sm"
                                />
                            </FieldGroup>
                        )}
                    </div>
                )}

                {/* Basic Auth */}
                {activeView === 'basic_auth' && isEnabled('basic_auth') && (
                    <div className="rounded-lg border border-slate-200 p-4">
                        <p className="text-sm text-slate-600">
                            Basic Auth uses standard <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">base64(username:password)</code> encoding.
                            Users will provide their credentials when creating a connection.
                        </p>
                    </div>
                )}

                {/* Bearer Token */}
                {activeView === 'bearer_token' && isEnabled('bearer_token') && (
                    <div className="rounded-lg border border-slate-200 p-4">
                        <p className="text-sm text-slate-600">
                            Bearer tokens are sent in the <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">Authorization: Bearer {'<token>'}</code> header.
                            Users will provide their token when creating a connection.
                        </p>
                    </div>
                )}

                {/* OAuth2 Config */}
                {activeView === 'oauth2' && isEnabled('oauth2') && (
                    <div className="space-y-4 rounded-lg border border-slate-200 p-4">
                        <h4 className="text-sm font-semibold text-slate-700">OAuth 2.0 Configuration</h4>

                        <div className="grid grid-cols-2 gap-3">
                            <FieldGroup label="Grant Type" required>
                                <Select
                                    value={value.oauth2GrantType}
                                    onValueChange={(v) => update({ oauth2GrantType: v as OAuth2GrantType })}
                                    disabled={disabled}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="authorization_code">Authorization Code</SelectItem>
                                        <SelectItem value="client_credentials">Client Credentials</SelectItem>
                                    </SelectContent>
                                </Select>
                            </FieldGroup>

                            <FieldGroup label="Token Request Auth" required>
                                <RadioGroup
                                    value={value.oauth2AuthMethod}
                                    onValueChange={(v) => update({ oauth2AuthMethod: v as OAuth2AuthMethod })}
                                    disabled={disabled}
                                    className="flex gap-3 pt-1.5"
                                >
                                    <div className="flex items-center gap-1.5">
                                        <RadioGroupItem value="basic_header" id="oauth2-basic" />
                                        <Label htmlFor="oauth2-basic" className="text-xs font-normal cursor-pointer">
                                            Basic Header
                                        </Label>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <RadioGroupItem value="body" id="oauth2-body" />
                                        <Label htmlFor="oauth2-body" className="text-xs font-normal cursor-pointer">
                                            In Body
                                        </Label>
                                    </div>
                                </RadioGroup>
                            </FieldGroup>
                        </div>

                        {value.oauth2GrantType === 'authorization_code' && (
                            <div className="grid grid-cols-2 gap-3">
                                <FieldGroup label="Authorization URL" required>
                                    <Input
                                        placeholder="https://provider.com/oauth/authorize"
                                        value={value.authorizationUrl}
                                        onChange={(e) => update({ authorizationUrl: e.target.value })}
                                        disabled={disabled}
                                    />
                                </FieldGroup>
                                <FieldGroup label="Callback URL">
                                    <Input
                                        placeholder="https://your-app.com/callback"
                                        value={value.callbackUrl}
                                        onChange={(e) => update({ callbackUrl: e.target.value })}
                                        disabled={disabled}
                                    />
                                </FieldGroup>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <FieldGroup label="Access Token URL" required>
                                <Input
                                    placeholder="https://provider.com/oauth/token"
                                    value={value.accessTokenUrl}
                                    onChange={(e) => update({ accessTokenUrl: e.target.value })}
                                    disabled={disabled}
                                />
                            </FieldGroup>
                            {value.oauth2GrantType === 'authorization_code' ? (
                                <FieldGroup label="Refresh Token URL">
                                    <Input
                                        placeholder="https://provider.com/oauth/token"
                                        value={value.refreshTokenUrl}
                                        onChange={(e) => update({ refreshTokenUrl: e.target.value })}
                                        disabled={disabled}
                                    />
                                </FieldGroup>
                            ) : (
                                <FieldGroup label="Audience">
                                    <Input
                                        placeholder="https://api.provider.com"
                                        value={value.audience}
                                        onChange={(e) => update({ audience: e.target.value })}
                                        disabled={disabled}
                                    />
                                </FieldGroup>
                            )}
                        </div>

                        <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
                            <p className="text-xs text-blue-700">
                                Client ID and Client Secret are provided per-connection when users set up a connection to this integration.
                            </p>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <FieldGroup label="Scopes">
                                <Input
                                    placeholder="read, write, admin (comma-separated)"
                                    value={value.scopes}
                                    onChange={(e) => update({ scopes: e.target.value })}
                                    disabled={disabled}
                                />
                            </FieldGroup>
                            {value.oauth2GrantType === 'authorization_code' && (
                                <FieldGroup label="Audience">
                                    <Input
                                        placeholder="https://api.provider.com"
                                        value={value.audience}
                                        onChange={(e) => update({ audience: e.target.value })}
                                        disabled={disabled}
                                    />
                                </FieldGroup>
                            )}
                        </div>
                    </div>
                )}

                {/* Custom Headers (all auth types except none) */}
                {hasConfigPanel && (
                    <div className="space-y-1.5">
                        <Label className="text-sm font-semibold">Custom Auth Headers</Label>
                        <p className="text-xs text-muted-foreground">
                            Additional headers sent with every authenticated request.
                        </p>
                        <KeyValuePairList
                            value={value.customHeaders}
                            onChange={(customHeaders) => update({ customHeaders })}
                            keyPlaceholder="Header name"
                            valuePlaceholder="Header value"
                            addLabel="Add Header"
                            disabled={disabled}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
